import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { BadgeCheck, Download, TrendingUp } from "lucide-react";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

function Cell({ label, value, color, testid }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1 font-display text-2xl" style={color ? { color } : {}} data-testid={testid}>{value}</div>
    </div>
  );
}

export default function SellerPlanPanel() {
  const [plan, setPlan] = useState(null);
  const [earn, setEarn] = useState(null);

  useEffect(() => {
    api.get("/seller/plan").then((r) => setPlan(r.data)).catch(() => {});
    api.get("/seller/earnings").then((r) => setEarn(r.data)).catch(() => {});
  }, []);
  if (!plan) return null;
  const limitHit = plan.listing_limit && plan.active_listings >= plan.listing_limit;

  return (
    <div className="mt-6 grid lg:grid-cols-[320px_1fr] gap-4" data-testid="plan-panel">
      <div className="hairline p-6" style={limitHit ? { borderColor: "#EA580C" } : {}}>
        <div className="flex items-center gap-2">
          <BadgeCheck size={15} style={{ color: "#1E3A2B" }} />
          <span className="label-eyebrow">{plan.name} plan</span>
        </div>
        <div className="mt-3 font-display text-3xl tracking-tight" data-testid="plan-name">{plan.name}</div>
        <div className="mt-1 text-sm text-neutral-600">
          No commission ·{" "}
          <span data-testid="plan-usage">
            {plan.active_listings}{plan.listing_limit ? `/${plan.listing_limit}` : ""} active listings
          </span>
        </div>
        <div className="mt-2 text-xs" style={{ color: plan.private_fee > 0 ? "#1E3A2B" : "#16A34A" }} data-testid="plan-private-fee">
          {plan.private_fee > 0
            ? `Private sales: ${(plan.private_fee * 100).toFixed(0)}% seller fee — upgrade to Pro to remove it.`
            : "Private sales: no seller fee on your plan."}
          {" "}Private collection: {plan.private_listings || 0}/{plan.private_limit} items.
        </div>
        {plan.key === "free" && plan.private_sales_count > 0 && (() => {
          const proPrice = (plan.plans?.find((p) => p.key === "pro")?.price) || 7.99;
          const proYear = proPrice * 12;
          const projFees = (plan.private_fees_paid || 0) * 12;
          const saves = projFees > proYear;
          return (
          <div className="mt-3 hairline p-3" style={{ borderColor: "#EA580C", background: "#FFF7ED" }}
            data-testid="private-fee-nudge">
            <div className="text-xs" style={{ color: "#9A3412" }}>
              You've paid <strong data-testid="private-fees-paid">{money(plan.private_fees_paid)}</strong> in private-sale
              fees across {plan.private_sales_count} sale{plan.private_sales_count === 1 ? "" : "s"}.
              Pro removes this fee entirely — keep 100% of every private sale.
            </div>
            <div className="text-xs mt-1.5" style={{ color: saves ? "#166534" : "#9A3412" }} data-testid="private-fee-projection">
              At this pace that's about <strong>{money(projFees)}/yr</strong> in fees vs <strong>{money(proYear)}/yr</strong> for Pro
              {saves ? <> — Pro would save you <strong data-testid="private-fee-savings">{money(projFees - proYear)}/yr</strong>.</> : "."}
            </div>
            <Link to="/pricing" className="btn-klein text-[0.6rem] mt-2 inline-flex" data-testid="private-fee-upgrade-btn">
              Upgrade to Pro
            </Link>
          </div>
          );
        })()}
        {plan.expires_at && (
          <div className="mt-1 text-xs text-neutral-500">Active until {new Date(plan.expires_at).toLocaleDateString()}</div>
        )}
        {limitHit && (
          <p className="mt-3 text-xs" style={{ color: "#EA580C" }} data-testid="plan-limit-warning">
            You've hit your {plan.listing_limit}-listing limit. Upgrade to keep listing.
          </p>
        )}
        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="/pricing" className="btn-klein text-[0.62rem]" data-testid="plan-upgrade-btn">
            {plan.key === "store" ? "Manage plan" : "Upgrade plan"}
          </Link>
          {plan.export && (
            <a href={`${API}/seller/export`} className="btn-outline text-[0.62rem]" data-testid="plan-export-btn">
              <Download size={12} /> CSV export
            </a>
          )}
        </div>
      </div>

      <div className="hairline p-6">
        <div className="flex items-center gap-2">
          <TrendingUp size={15} />
          <span className="label-eyebrow">Earnings after fees</span>
        </div>
        {!earn || earn.sales === 0 ? (
          <p className="mt-3 text-sm text-neutral-500" data-testid="earnings-empty">
            No sales yet. When you sell, you'll see your gross, any designer authentication fee and your net
            payout here — with no seller commission taken.
          </p>
        ) : (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-6">
            <Cell label="Gross" value={money(earn.gross)} testid="earn-gross" />
            <Cell label="Auth fees" value={`−${money(earn.auth_fees)}`} testid="earn-authfees" />
            <Cell label="Net" value={money(earn.net)} color="#16A34A" testid="earn-net" />
            <Cell label="On hold" value={money(earn.held)} color="#EA580C" testid="earn-held" />
          </div>
        )}
      </div>
    </div>
  );
}
